/**
 * Le chargement — combien de plancher une marchandise occupe.
 *
 * Fonctions pures : ce qui est ici se vérifie sans navigateur, sans base et
 * sans session.
 *
 * **CE FICHIER EST DANS `packages/core` ET NON DANS `apps/web`**, depuis le
 * 05/09/2026. Il y a été déplacé le jour où l'utilisateur a tranché : « lorsque
 * l'utilisateur met 10 palettes 80×120, l'app doit calculer automatiquement le
 * ML correspondant et le mètre à coter ». Ce calcul a donc DEUX appelants :
 *
 *   - **l'API**, qui l'exécute à la saisie d'une étape pour FIGER le ML
 *     unitaire dessus — le chiffre qu'on facture ne doit pas bouger le jour où
 *     quelqu'un corrige les dimensions d'un support ;
 *   - **l'écran**, qui l'exécute pendant qu'on tape pour MONTRER le total.
 *
 * Deux exemplaires de ce calcul finiraient par ne plus dire la même chose, et
 * l'écart se verrait sur une facture. Un seul fichier, deux appelants.
 *
 *
 * LE MÈTRE LINÉAIRE EST UNE CONVENTION, PAS UNE GÉOMÉTRIE
 * ---------------------------------------------------------------------------
 * Mot pour mot, de l'utilisateur le 04/09/2026 : **« 1 ML reste toujours 1 ML
 * peu importe le véhicule »**, et 1 ML vaut une bande de **100 × 240 cm**.
 *
 * La largeur de référence est donc **2,40 m**, et rien ici ne regarde la
 * remorque. C'est une correction de ce qui avait été conçu quelques heures plus
 * tôt : le calcul dépendait alors de la largeur et de la hauteur intérieures du
 * camion attelé. Une convention que le métier partage vaut mieux qu'un calcul
 * exact que personne n'emploie.
 *
 * Ce que cette convention rend possible, et qui compte pour la suite : **le ML
 * d'un support est une constante**, donc il se fige sur une étape au moment de
 * la saisie, alors même qu'on ne sait pas encore quel camion partira.
 *
 *
 * ON ESSAIE LES DEUX ORIENTATIONS, TOUJOURS
 * ---------------------------------------------------------------------------
 * Une palette se pose dans un sens ou dans l'autre, et le quai choisit le plus
 * économe. Ne regarder qu'une orientation donne 0,60 ML pour une palette
 * 100 × 120 au lieu de 0,50 — erreur commise le 04/09 dans la première maquette,
 * corrigée en refaisant le calcul plutôt qu'en relisant la phrase.
 *
 *
 * LES ÉTAGES SE SAISISSENT
 * ---------------------------------------------------------------------------
 * La règle « étages = hauteur de la caisse ÷ hauteur du support » a été
 * proposée puis écartée par l'utilisateur : elle donnait trois étages pour une
 * demi-palette de 90 cm sous 2,70 m, ce qui est vrai géométriquement et faux
 * sur un quai. Le logiciel ne sait pas ce que la marchandise supporte.
 *
 *
 * CE QUE CE FICHIER NE FAIT PAS
 * ---------------------------------------------------------------------------
 * Il ne parcourt pas les arrêts d'une tournée et ne cherche pas le point le
 * plus chargé : cela viendra avec la table `tournees`. Ici, seulement de quoi
 * répondre à « combien un support occupe » et « qu'est-ce qui rentre dans cette
 * remorque ».
 */

/** La largeur sur laquelle le mètre linéaire se compte. Deux mètres quarante. */
export const LARGEUR_DE_REFERENCE_CM = 240;

/**
 * Un support, tel que l'API le rend. Les dimensions sont en CENTIMÈTRES et
 * facultatives — « une 80×120 » se saisit 120 × 80.
 */
export interface Support {
  readonly nom: string;
  readonly abrege: string | null;
  readonly longueur_cm: number | null;
  readonly largeur_cm: number | null;
  readonly hauteur_cm: number | null;
  /** Saisi, jamais déduit. 1 = on ne gerbe pas. */
  readonly etages: number;
}

/** Comment une unité se pose, et ce qu'elle coûte en plancher. */
export interface Pose {
  /** Combien tiennent côte à côte dans les 240 cm. */
  readonly deFront: number;
  /** Ce que la rangée consomme en longueur, en centimètres. */
  readonly profondeurCm: number;
  /** La dimension posée en travers du camion, en centimètres. */
  readonly traversCm: number;
  readonly etages: number;
  /** Les mètres linéaires qu'UNE unité occupe. */
  readonly ml: number;
}

/**
 * Pourquoi un support ne se pose pas. Le mot dit la cause, l'écran l'écrit en
 * français — les codes ne s'affichent jamais tels quels, c'est la règle du
 * dépôt depuis le 03/09/2026.
 */
export type RefusDePose = 'dimensions_absentes' | 'trop_large' | 'etages_invalides';

export type Resultat = { readonly pose: Pose } | { readonly refus: RefusDePose };

/** Vrai quand le support n'a pas pu être posé. */
export function refuse(r: Resultat): r is { readonly refus: RefusDePose } {
  return 'refus' in r;
}

/**
 * Où une unité de ce support se pose, et combien de mètres linéaires elle prend.
 *
 * **La hauteur n'entre pas dans ce calcul.** Elle sert au dessin isométrique,
 * pas au plancher — le gerbage passe par `etages`, qui est saisi.
 *
 * Les deux orientations sont essayées et la plus économe est retenue. Quand
 * elles se valent — le cas d'une palette EUR, qui donne 0,40 ML dans les deux
 * sens —, la première l'emporte, celle qui met la LARGEUR en travers : c'est
 * ainsi qu'on charge trois palettes de front, et le dessin doit s'y conformer.
 */
export function poser(support: Support): Resultat {
  const { longueur_cm: longueur, largeur_cm: largeur, etages } = support;

  // `etages` est `not null default 1` en base et borné entre 1 et 10. On le
  // vérifie quand même : ce fichier ne suppose jamais que la donnée vient de
  // notre base — une réponse d'API tronquée ou un jeu d'essai bâclé passeraient
  // ici, et une division par zéro rendrait `Infinity` sans rien dire.
  if (!Number.isFinite(etages) || etages < 1) {
    return { refus: 'etages_invalides' };
  }

  if (
    longueur === null ||
    largeur === null ||
    !Number.isFinite(longueur) ||
    !Number.isFinite(largeur) ||
    longueur <= 0 ||
    largeur <= 0
  ) {
    return { refus: 'dimensions_absentes' };
  }

  const essais = [
    { traversCm: largeur, profondeurCm: longueur },
    { traversCm: longueur, profondeurCm: largeur },
  ]
    .map((o) => ({
      ...o,
      deFront: Math.floor(LARGEUR_DE_REFERENCE_CM / o.traversCm),
    }))
    .filter((o) => o.deFront > 0);

  if (essais.length === 0) {
    // Les deux côtés dépassent 240 cm. La base l'accepte — elle ne sait pas ce
    // qu'on chargera demain —, le calcul ne peut rien en dire.
    return { refus: 'trop_large' };
  }

  // Tri STABLE sur le coût : à égalité, `sort` de JavaScript garde l'ordre
  // d'origine depuis ES2019, donc la première orientation gagne. C'est ce qui
  // fait qu'une palette EUR se pose à trois de front et non à deux.
  //
  // `reduce` plutôt que `sort(...)[0]` : sous `noUncheckedIndexedAccess`, le
  // premier élément d'un tableau est `T | undefined` pour TypeScript, qui ne
  // sait pas que `essais` n'est pas vide — on vient pourtant de le vérifier
  // juste au-dessus. `reduce` avec une graine explicite dit la même chose sans
  // laisser un `!` traîner dans le code.
  const meilleure = essais.reduce((a, b) =>
    b.profondeurCm / b.deFront < a.profondeurCm / a.deFront ? b : a,
  );

  // LE MÈTRE LINÉAIRE À LA SURFACE RÉELLEMENT OCCUPÉE — décision de Mohamed
  // le 31/10/2026 (cadrage de la saisie d'OT, §9) : « à la surface réellement
  // utilisée pour les ml ».
  //
  //     ML unitaire = longueur × largeur ÷ 2,40 m ÷ étages
  //
  // Les formats classiques ne bougent pas : 80×120 → 0,40 ; 100×120 → 0,50 ;
  // 80×60 → 0,20 ; le support « Mètre linéaire » 100×240 → 1,00. Ce qui
  // change, ce sont les formats qui ne tombent pas juste dans 2,40 m : une
  // pièce de 590 × 124 compte 3,05 ML et non plus 5,90 — les 116 cm restant
  // à côté servent à d'autres marchandises.
  //
  // L'ancienne règle comptait la RANGÉE entière (profondeur ÷ nombre de
  // front). L'orientation reste calculée au-dessus : elle sert au dessin de la
  // préparation (`deFront`, `profondeurCm`), et le refus « trop large » tient
  // toujours — un support plus large que 2,40 m dans les deux sens ne se
  // charge pas, quelle que soit la formule.
  //
  // EFFET SUR LE PASSÉ : AUCUN. Le ML est figé sur l'étape à la saisie
  // (`etapes.ml_unitaire`) ; seules les nouvelles saisies prennent cette règle.
  const surfaceCm2 = longueur * largeur;

  return {
    pose: {
      deFront: meilleure.deFront,
      profondeurCm: meilleure.profondeurCm,
      traversCm: meilleure.traversCm,
      etages,
      ml: surfaceCm2 / LARGEUR_DE_REFERENCE_CM / 100 / etages,
    },
  };
}

/**
 * Les mètres linéaires d'une unité, ou `null` si le support ne se pose pas.
 *
 * Le raccourci qu'emploient les écrans : la colonne « ML / unité » des
 * Paramètres, et plus tard le cumul d'une tournée.
 */
export function metresLineaires(support: Support): number | null {
  const r = poser(support);
  return refuse(r) ? null : r.pose.ml;
}

/**
 * Combien d'unités de ce support tiennent dans une capacité donnée.
 *
 * `capaciteMl` est celle de la remorque — 13,20 pour une semi. Rend `null`
 * quand le support ne se pose pas ou que la capacité est inconnue : **l'écran
 * doit alors se taire, pas afficher zéro**. Un zéro se lit « ça ne rentre pas »,
 * ce qui est une réponse, alors qu'on n'en a aucune.
 */
export function combienTiennent(
  support: Support,
  capaciteMl: number | null,
): number | null {
  if (capaciteMl === null || !Number.isFinite(capaciteMl) || capaciteMl <= 0) {
    return null;
  }
  const ml = metresLineaires(support);
  if (ml === null || ml <= 0) return null;

  // `Math.floor` sur une division de flottants : 13.20 / 0.40 vaut
  // 32.99999999999999 en virgule flottante, et donnerait 32 palettes au lieu de
  // 33. Constaté en écrivant le test. On arrondit à six décimales avant de
  // tronquer — largement au-delà de la précision d'un centimètre, largement en
  // deçà de l'erreur de représentation.
  return Math.floor(Number((capaciteMl / ml).toFixed(6)));
}

/**
 * Les mètres linéaires occupés par une quantité d'un support.
 *
 * Rend `null` si le support ne se pose pas — et non zéro, pour la même raison
 * que ci-dessus.
 */
export function mlOccupes(support: Support, quantite: number): number | null {
  if (!Number.isFinite(quantite) || quantite < 0) return null;
  const ml = metresLineaires(support);
  return ml === null ? null : ml * quantite;
}

/**
 * Un nombre de mètres linéaires, écrit comme on le lit : « 0,40 », « 13,20 ».
 *
 * Toujours deux décimales, y compris sur un compte rond : dans une colonne,
 * « 13,2 » et « 0,40 » ne s'alignent pas.
 */
export function mlLisible(ml: number | null): string {
  if (ml === null || !Number.isFinite(ml)) return '—';
  // « -0,00 » CONSTATÉ À L'ÉCRAN le 05/09/2026, sur le dernier arrêt d'une
  // tournée entièrement livrée : le cumul y valait -1,7e-15 et non zéro, la
  // soustraction de flottants ne retombant pas juste. Un camion vide qui
  // affiche un reste négatif fait douter de tout le reste de la page. On
  // ramène à zéro tout ce qui est sous le demi-centimètre, la précision
  // au-delà de laquelle le mètre linéaire n'a de toute façon aucun sens.
  const arrondi = Math.abs(ml) < 0.005 ? 0 : ml;
  return arrondi.toFixed(2).replace('.', ',');
}
